import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';
import DataGridComponent from 'components/DataGridComponent';


const Shifts = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
  const canCreate = hasRight(permissionMap, 'Shifts', 'create', false);
  const canEdit   = hasRight(permissionMap, 'Shifts', 'update', false);
  const canDelete = hasRight(permissionMap, 'Shifts', 'delete', false);

  const scrollRef = useRef(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [shifts, setShifts]     = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  useEffect(() => {
    let isMounted = true;
    const fetchShifts = async () => {
      try {
        setLoading(true);
        const res = await api.get('/shifts');
        isMounted && setShifts(res.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchShifts();
    return () => { isMounted = false; };
  }, []);

  const columns = useMemo(() => [
    {
      field: 'no',
      headerName: 'No',
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(isMobile ? { width: 50 } : { flex: 0.5 }),
    },
    {
      field: 'shiftCode',
      headerName: 'Code',
      ...(isMobile ? { width: 80 } : { flex: 1 }),
    },
    {
      field: 'shiftName',
      headerName: 'Name',
      ...(isMobile ? { width: 150 } : { flex: 2 }),
    },
    {
      field: 'workMode',
      headerName: 'Mode',
      ...(isMobile ? { width: 80 } : { flex: 1 }),
    },
    {
      field: 'startTime',
      headerName: 'Start',
      align: 'center',
      headerAlign: 'center',
      ...(isMobile ? { width: 70 } : { flex: 1 }),
    },
    {
      field: 'endTime',
      headerName: 'End',
      align: 'center',
      headerAlign: 'center',
      ...(isMobile ? { width: 70 } : { flex: 1 }),
    },
    {
      field: 'workingTime',
      headerName: 'Hours',
      align: 'right',
      headerAlign: 'right',
      ...(isMobile ? { width: 70 } : { flex: 1 }),
    },
    {
      field: 'paidFactor',
      headerName: 'Paid×',
      align: 'right',
      headerAlign: 'right',
      ...(isMobile ? { width: 70 } : { flex: 1 }),
    },
    {
      field: 'color',
      headerName: 'Color',
      align: 'center',
      headerAlign: 'center',
      ...(isMobile ? { width: 80 } : { flex: 1 }),
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {params.value && (
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: params.value,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Active',
      align: 'center',
      headerAlign: 'center',
      ...(isMobile ? { width: 70 } : { flex: 1 }),
      renderCell: (params) => params.value ? '✓' : '✗',
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];
    return [{
      field: 'action',
      headerName: 'Action',
      filterable: false,
      sortable: false,
      width: 60 + activeCount * 25,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1} sx={{ width: '100%', height: '100%' }}>
          {canEdit && (
            <Tooltip title="Edit" arrow>
              <IconButton sx={{ color: 'primary.dark' }} onClick={() => navigate(`/shifts/${params.row._id}`)}>
                <Edit />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete" arrow>
              <IconButton sx={{ color: 'primary.dark' }} onClick={() => handleDeleteClick(params.row._id)}>
                <DeleteForever />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    }];
  }, [navigate, canEdit, canDelete]);

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, shifts);
    if (selectedIds.length === 0) { toast.error('No rows selected for deletion!'); return; }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;
      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.delete(`/shifts/delete/${idToDelete}`);
        setShifts(prev => prev.filter(s => s._id !== idToDelete));
        toast.success('Shift deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, shifts);
        if (selectedIds.length === 0) { toast.error('No rows selected!'); return; }
        const res = await api.patch('/shifts/soft-delete', { ids: selectedIds });
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted.`);
        setShifts(prev => prev.filter(s => !selectedIds.includes(s._id)));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete shift(s)!');
    }
  }, [canDelete, deleteType, idToDelete, rowSelectionModel, shifts]);

  if (loading) return <FlexCenter height={LAYOUT_HEIGHT}><CircularProgress /></FlexCenter>;
  if (error)   return <FlexCenter height={LAYOUT_HEIGHT}><Typography color="error">{error?.message}</Typography></FlexCenter>;

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header title="Shifts" subtitle={`List of ${shifts.length} Shifts`} />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, shifts)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate('/shifts/new')}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>

      <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap={2}>
        <Box ref={scrollRef} height={LAYOUT_HEIGHT} sx={{ gridColumn: 'span 12', overflowY: 'auto' }}>
          {isMobile ? (
            <CardList
              rows={shifts}
              primaryField="shiftCode"
              fields={[
                { label: 'Name',  key: 'shiftName' },
                { label: 'Mode',  key: 'workMode' },
                { label: 'Start', key: 'startTime' },
                { label: 'End',   key: 'endTime' },
              ]}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={(id) => navigate(`/shifts/${id}`)}
              onDelete={handleDeleteClick}
            />
          ) : (
            <DataGridComponent
              rows={shifts}
              columns={columns.concat(actionColumn)}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                sx: {
                  '& .MuiDataGrid-row.isActive-false': {
                    backgroundColor: `${theme.palette.primary.light} !important`,
                  },
                },
              }}
            />
          )}
          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      </Box>

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Shifts;
